<template>
    <div>
        <PageTemplate class="quickscan-page">
            <BaseSection
                section-width="page"
                :data-theme="onFirstQuestion ? 'dark-blue' : ''"
                class="quickscan-intro"
            >
                <BaseRichText :wide="true">
                    <h1>{{ entry.title ?? 'Quickscan' }}</h1>

                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        v-if="entry.hero && onFirstQuestion"
                        v-html="entry.hero"
                    />
                    <!-- eslint-enable vue/no-v-html -->
                </BaseRichText>
            </BaseSection>

            <TheQuickscan
                v-if="current"
                :quickscan="entry.slug"
                :current="current"
                :max-nr-questions="maxNrQuestions"
            />
        </PageTemplate>
    </div>
</template>

<script setup>
import { trim } from 'lodash-es';
import { quickscanTimeline } from '~/components/TheQuickscan/timeline';

import EntryQuery from '~/graphql/queries/Entry.graphql';
import QuickscanQuery from '~/graphql/queries/Quickscan.graphql';

const router = useRouter();
const route = useRoute();

const path = computed(() => route?.path);
const trimmedPath = computed(() => trim(path.value, '/'));

const pageSlug = computed(() => {
    if (Array.isArray(route?.params?.slug)) {
        return route?.params?.slug?.[0];
    }

    return route?.params?.slug;
});
const questionSlug = computed(() => {
    if (Array.isArray(route?.params?.slug)) {
        return route?.params?.slug?.[1];
    }

    return route?.params?.slug;
});

const { variables: defaultVariables } = useCraftGraphql();

const pageVariables = computed(() => {
    return {
        ...defaultVariables,
        section: 'quickscan',
        uri: null,
        slug: pageSlug.value,
    };
});

const pageResponse = await useAsyncQuery({
    query: EntryQuery,
    variables: pageVariables
});

const { craftEntry: entry } = useSeoCraftPage(pageResponse);
useCallToActions(pageResponse);

const quickscanVariables = computed(() => {
    return {
        descendantOf: parseInt(entry.value.id, 10)
    };
});

const { data: quickscanData } = await useAsyncQuery({
    query: QuickscanQuery,
    variables: quickscanVariables
});

const filteredQuickscanData = computed(() => quickscanData.value?.entries?.filter(({ __typename }) => __typename === 'question_Entry' || __typename === 'quickscanResult_Entry'));

const questions = computed(() => filteredQuickscanData.value?.filter(({ __typename }) => __typename === 'question_Entry'));

const firstQuestion = computed(() => questions.value?.toSorted((a, b) => a.questionIndex-b.questionIndex)?.[0]);
const onFirstQuestion = computed(() => trimmedPath.value === entry.value.uri || firstQuestion.value.uri === path.value);

const maxNrQuestions = computed(() => questions.value?.reduce((acc, cur) => Math.max(acc, cur.questionIndex), 0));

const current = computed(() => {
    if (trimmedPath.value === entry.value.uri) {
        return firstQuestion.value;
    }

    return filteredQuickscanData.value?.find(({ slug: _slug }) => _slug === questionSlug?.value);
});

watchEffect(() => {
    // if there is no current, redirect to the start of the quickscan!
    if (current.value === undefined) {
        router.push({ name: 'quickscan-slug___nl', params: { slug: entry.value.slug } });
    }
});

onMounted(() => {
    if (onFirstQuestion.value) {
        quickscanTimeline.value = [];
    }
    // if the session history is empty and this is not the first question
    // redirect to the first question!
    if (!onFirstQuestion.value && quickscanTimeline.value.length === 0) {
        router.push({ name: 'quickscan-slug___nl', params: { slug: entry.value.slug } });
    }
});

useHead({
    bodyAttrs: {
        class: 'quickscan-page'
    }
});

usePopState(() => {
    quickscanTimeline.value?.pop();
});
</script>

<style lang="less">
.quickscan-intro {
    .base-rich-text p {
        color: currentColor;
    }
}
</style>
